import { css } from 'styled-components';
import { space, colorSet, mq } from '@ccg/core';
import adaptiveModularScale$1 from 'adaptive-modular-scale';
import { step } from '@4rk/modular-system';

/**
 * Returns adaptive modular font sizes
 * @param  {number} step - Step on the modular scale. Default: 0.
 * @param  {array} corrections - Corrections array. Default: null.
 * @returns {string} Resulting adaptive modular font sizes
 * @example ${fontSize(3, [-1, 2])};
 */

const fontSize = (step = 0, corrections = null) => ({
  theme
}) => {
  const {
    typography
  } = theme;
  const {
    scale: {
      width,
      base,
      ratio,
      breakpoints
    }
  } = typography;
  const corr = corrections ? {
    [step]: corrections
  } : {};
  return adaptiveModularScale$1(step, {
    ratio,
    base,
    width,
    breakpoints: parseFloat(breakpoints),
    corrections: corr
  })({
    theme
  });
};
const fontFamily = (key = 'default') => props => {
  const family = step('form', 'fontFamily')(props);
  return `font-family: ${family[key]};`;
};
/**
 * Returns font declarations
 * @param  {number} step - Step on the modular scale. Default: 0.
 * @param  {string} familyKey - Key of font family inside theme.fonts object. Default: "default".
 * @returns {string} Resulting font declarations
 * @example ${font(3, "accent")};
 */

const font = (step = 0, familyKey = 'default') => ({
  theme
}) => {
  const {
    typography: {
      scale: {
        corrections
      }
    }
  } = theme;
  const corr = corrections && corrections[familyKey] ? corrections[familyKey][step] : null;
  const family = fontFamily(familyKey)({
    theme
  });
  const fontSizes = fontSize(step, corr)({
    theme
  });
  return `${family}\n${fontSizes}`;
};
/* ACCENTS */

const getAccentBottomLine = css(["border-bottom:", " solid ", ";background:linear-gradient(180deg,transparent 78%,", " 78%);"], space(1), colorSet('accent'), colorSet('accent'));
const getAccentTopLine = css(["position:relative;margin-top:", ";&:before{content:'';position:relative;background:", ";width:", ";height:", ";margin-bottom:", ";display:block;}"], space(4), colorSet('accent'), space(25), space(2), space(2));
/* COPIES */

const small = css(["", " line-height:1.5;letter-spacing:0.3px;font-weight:bold;"], font(-1));
const copy = css(["", " line-height:1.65;letter-spacing:0.3px;", ""], font(0), props => props.transform === 'uppercase' && css(["font-weight:bold;line-height:1.4;letter-spacing:", ";"], space(1.25)));
const copyIntro = css(["", " line-height:1.8;letter-spacing:-0.1px;"], font(1));
const subline = css(["", " line-height:1.27;font-weight:bold;"], font(1));
const sublineBig = css(["font-size:1.875rem;line-height:1.33;font-weight:bold;", "{font-size:1.813rem;}"], mq('m'));
const subheadline = css(["", " line-height:1.3;font-weight:bold;"], font(2));
const quote = css(["", " line-height:1.2;letter-spacing:-0.3px;"], font(3, 'accent'));
const quoteBig = css(["", " line-height:1.07;letter-spacing:-0.5px;"], font(4, 'accent'));
const intro = css(["", " line-height:1.14;letter-spacing:-0.3px;font-weight:bold;"], font(3));
const headline = css(["", " line-height:1.02;letter-spacing:-0.5px;font-weight:bold;"], font(4));
const hero = css(["", " line-height:1.02;letter-spacing:-0.5px;font-weight:bold;", "{", " line-height:0.99;letter-spacing:-0.5px;font-weight:bold;}"], font(4), mq('m'), font(5));
const getTypography = {
  copy,
  copyIntro,
  intro,
  small,
  headline,
  subline,
  sublineBig,
  subheadline,
  hero,
  quote,
  quoteBig
};

export { copy, copyIntro, font, fontFamily, fontSize, getAccentBottomLine, getAccentTopLine, getTypography, headline, hero, intro, quote, quoteBig, small, subheadline, subline, sublineBig };
